<template>
  <v-dialog v-model="internalDialog" width="1400" persistent>
    <v-card>
      <v-card-title class="headline primary">
        <span class="white--text">Bulk Upload NBN Orders</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-6">
        <v-row v-if="importResult">
          <v-col cols="12">
            <v-alert :type="importAlertType" prominent>
              <div class="font-weight-bold mb-2">{{ importMessage }}</div>

              <v-divider v-if="importResult.failureCount > 0"
                         class="my-2"
                         style="border-color: rgba(255,255,255,0.3);" />

              <!-- Collapsible Failures -->
              <div v-if="importResult.failureCount > 0">
                <v-expansion-panels accordion flat class="mt-2 failed-panels">
                  <v-expansion-panel class="failed-panel">
                    <v-expansion-panel-header class="px-0 failed-header">
                      <strong>Failed ({{ importResult.failureCount }}):</strong>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="px-0 failed-content">
                      <ul v-if="importResult.failedDetails && importResult.failedDetails.length" class="mb-0">
                        <li v-for="(f, i) in importResult.failedDetails" :key="`${f.orderId}-${i}`">
                          <span class="font-weight-bold">Order ID :</span> "{{ f.orderId }}",
                          <span class="font-weight-bold">Reason :</span> "{{ f.reason }}"
                        </li>
                      </ul>

                      <div v-else class="red--text text--darken-4">N/A</div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>

            </v-alert>
          </v-col>
        </v-row>
        <!-- File Input -->
        <v-row>
          <v-col cols="12" md="6">
            <v-file-input v-model="csvFile"
                          label="Select CSV File"
                          accept=".csv"
                          outlined
                          dense
                          prepend-icon="mdi-file-delimited"
                          @change="onFileSelected"
                          :error-messages="fileError"></v-file-input>
          </v-col>
          <v-col cols="12" md="3">
            <v-btn class="primary mt-1"
                   @click="downloadTemplate">
              <v-icon left>mdi-download</v-icon>
              Download Template
            </v-btn>
          </v-col>
        </v-row>

        <!-- Summary -->
        <v-row v-if="parsedRows.length > 0" class="mb-2">
          <v-col cols="12">
            <v-alert type="info" dense outlined>
              Total: <strong>{{ parsedRows.length }}</strong> |
              Valid: <strong class="green--text">{{ validRows.length }}</strong> |
              Rejected: <strong class="red--text">{{ rejectedRows.length }}</strong>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Grid -->
        <div v-if="parsedRows.length > 0">
          <ag-grid-vue class="ag-theme-alpine"
                       style="height: 55vh; width: 100%"
                       :columnDefs="columnDefs"
                       :defaultColDef="defaultColDef"
                       :rowData="parsedRows"
                       :gridOptions="gridOptions"
                       @grid-ready="onGridReady"
                       :enableCellTextSelection="true"
                       :ensureDomOrder="true"
                       :getRowStyle="getRowStyle"></ag-grid-vue>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <div class="d-flex justify-end pa-4">
        <v-btn v-if="!importResult"
               class="primary mr-4"
               :disabled="validRows.length === 0 || isSubmitting"
               :loading="isSubmitting"
               @click="submitOrders">
          Submit {{ validRows.length }} Valid Order(s)
        </v-btn>
        <v-btn @click="close">{{ importResult ? 'Close' : 'Cancel' }}</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
  import { AgGridVue } from "ag-grid-vue";
  import "ag-grid-community/dist/styles/ag-grid.css";
  import "ag-grid-community/dist/styles/ag-theme-alpine.css";
  import {
    MANDATORY_COLUMNS,
    REQUIRED_FIELDS,
    VALID_PRODUCT_TYPES,
    VALID_ORDER_TYPES,
    DATE_REGEX
  } from "../../constants/nbn-bulk-upload-constants";

  export default {
    name: "nbn-bulk-upload",
    components: { AgGridVue },
    props: {
      showDialog: { type: Boolean, required: true }
    },
    data: () => ({
      internalDialog: false,
      csvFile: null,
      fileError: "",
      parsedRows: [],
      gridOptions: {},
      gridApi: null,
      isSubmitting: false,
      importResult: null,
      importMessage: "",
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 120
      }
    }),
    watch: {
      showDialog(val) {
        this.internalDialog = val;
      },
      internalDialog(val) {
        if (!val) {
          this.$emit("closeBulkUpload");
        }
      }
    },
    computed: {
      columnDefs() {
        let vm = this;
        const cols = MANDATORY_COLUMNS.map(col => {
          const colDef = {
            headerName: col,
            field: col,
            maxWidth: col === "CustomerContactEmail" ? 220 : 180,
            cellStyle: params => {
              if (
                REQUIRED_FIELDS.includes(col) &&
                vm.isEmpty(params.value)
              ) {
                return { backgroundColor: "#ffcdd2" };
              }
              if (col === "CustomerAuthDate" && params.value && !DATE_REGEX.test(params.value)) {
                return { backgroundColor: "#ffcdd2" };
              }
              return null;
            }
          };

          if (col === "CustomerAuthDate") {
            colDef.valueFormatter = params => {
              if (!params.value) return "";
              return params.value;
            };
          }

          return colDef;
        });

        cols.unshift({
          headerName: "Status",
          field: "_status",
          maxWidth: 120,
          pinned: "left",
          cellStyle: params => {
            if (params.value === "Rejected") {
              return { color: "red", fontWeight: "bold" };
            }
            return { color: "green", fontWeight: "bold" };
          }
        });

        cols.push({
          headerName: "Errors",
          field: "_errors",
          minWidth: 280,
          cellStyle: () => ({ color: "red", fontSize: "12px" })
        });

        return cols;
      },
      validRows() {
        let vm = this;
        return vm.parsedRows.filter(r => r._status === "Valid");
      },
      rejectedRows() {
        let vm = this;
        return vm.parsedRows.filter(r => r._status === "Rejected");
      },
      importAlertType() {
        let vm = this;
        if (!vm.importResult) return "info";
        if (vm.importResult.failureCount === 0) return "success";
        if (vm.importResult.successCount > 0) return "warning";
        return "error";
      }
    },
    methods: {
      onGridReady(params) {
        let vm = this;
        vm.gridApi = params.api;
        vm.gridApi.sizeColumnsToFit();
      },
      getRowStyle(params) {
        if (params.data._status === "Rejected") {
          return { backgroundColor: "#fff3f3" };
        }
        return null;
      },
      isEmpty(val) {
        if (val === null || val === undefined) return true;
        const s = String(val).trim();
        return s === "" || s.toLowerCase() === "n/a";
      },
      formatDate(raw) {
        if (!raw) return "";
        const trimmed = String(raw).trim();

        const m = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!m) return "";

        const dd = Number(m[1]);
        const mm = Number(m[2]);
        const yyyy = Number(m[3]);

        if (mm < 1 || mm > 12) return "";
        if (dd < 1 || dd > 31) return "";

        const dt = new Date(Date.UTC(yyyy, mm - 1, dd));
        const isValid =
          dt.getUTCFullYear() === yyyy &&
          dt.getUTCMonth() === (mm - 1) &&
          dt.getUTCDate() === dd;

        if (!isValid) return "";

        return `${String(yyyy).padStart(4, "0")}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
      },
      validateRow(row) {
        let vm = this;
        const errors = [];

        REQUIRED_FIELDS.forEach(field => {
          if (vm.isEmpty(row[field])) {
            errors.push(`${field} is required`);
          }
        });

        if (row.CustomerAuthDate && !DATE_REGEX.test(row.CustomerAuthDate)) {
          errors.push("CustomerAuthDate must be yyyy-MM-dd");
        }

        if (row.ProductType && !VALID_PRODUCT_TYPES.includes(row.ProductType)) {
          errors.push(`Invalid ProductType: ${row.ProductType}`);
        }

        if (row.OrderType && !VALID_ORDER_TYPES.includes(row.OrderType)) {
          errors.push(`Invalid OrderType: ${row.OrderType}`);
        }

        return errors;
      },
      onFileSelected(file) {
        let vm = this;
        vm.parsedRows = [];
        vm.fileError = "";
        vm.importResult = null;
        vm.importMessage = "";

        if (!file) return;

        if (!file.name.toLowerCase().endsWith(".csv")) {
          vm.fileError = "Please select a .csv file";
          return;
        }

        const reader = new FileReader();
        reader.onload = e => {
          vm.parseCSV(e.target.result);
        };
        reader.onerror = () => {
          vm.fileError = "Failed to read file";
        };
        reader.readAsText(file);
      },
      parseCSV(text) {
        let vm = this;
        const MAX_UPLOAD_ROWS = 50;
        const lines = text
          .split(/\r?\n/)
          .map(l => l.trim())
          .filter(l => l.length > 0);

        const dataRowCount = lines.length;

        if (dataRowCount < 2) {
          vm.fileError = "CSV must contain a header row and at least one data row";
          return;
        }

        if (dataRowCount > MAX_UPLOAD_ROWS) {
          vm.fileError = `CSV contains ${dataRowCount} data rows. Maximum allowed is ${MAX_UPLOAD_ROWS}. Please reduce the file and try again.`;
          vm.csvFile = null;
          return;
        }

        const headers = vm.parseCSVLine(lines[0]);

        const missing = MANDATORY_COLUMNS.filter(
          col => !headers.some(h => h.trim() === col)
        );
        if (missing.length > 0) {
          vm.fileError = `Missing columns: ${missing.join(", ")}`;
          return;
        }

        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const values = this.parseCSVLine(lines[i]);
          const row = {};

          headers.forEach((h, idx) => {
            const key = h.trim();
            row[key] = values[idx] !== undefined ? values[idx].trim() : "";
          });

          if (vm.isEmpty(row.FibreUpgrade)) {
            row.FibreUpgrade = "No";
          }

          ["CvcId", "NbnOrderId", "AvcId", "PriId"].forEach(f => {
            if (vm.isEmpty(row[f])) {
              row[f] = "N/A";
            }
          });

          row.CustomerAuthDate = vm.formatDate(row.CustomerAuthDate);
          const errors = vm.validateRow(row);
          row._status = errors.length > 0 ? "Rejected" : "Valid";
          row._errors = errors.join("; ");
          row._rowIndex = i;

          rows.push(row);
        }

        vm.parsedRows = rows;
      },
      parseCSVLine(line) {
        const result = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (inQuotes) {
            if (ch === '"' && i + 1 < line.length && line[i + 1] === '"') {
              current += '"';
              i++;
            } else if (ch === '"') {
              inQuotes = false;
            } else {
              current += ch;
            }
          } else {
            if (ch === '"') {
              inQuotes = true;
            } else if (ch === ",") {
              result.push(current);
              current = "";
            } else {
              current += ch;
            }
          }
        }
        result.push(current);
        return result;
      },
      async submitOrders() {
        let vm = this;
        vm.isSubmitting = true;
        vm.importResult = null;
        vm.importMessage = "";

        try {
          const orders = vm.validRows.map(row => ({
            wholesalerAccountNumber: parseInt(row.WholesalerAccountNumber) || 0,
            customerFirstname: row.CustomerFirstname,
            customerSurname: row.CustomerSurname,
            customerContactEmail: row.CustomerContactEmail,
            customerContactNumber: row.CustomerContactNumber,
            customerReference: row.CustomerReference,
            customerAuthReference: row.CustomerAuthReference,
            productType: row.ProductType,
            orderType: row.OrderType,
            customerAuthDate: row.CustomerAuthDate,
            bandwidthProfile: row.BandwidthProfile,
            contractTerm: row.ContractTerm,
            hasStaticIp: row.HasStaticIp,
            radiusUsername: row.RadiusUsername,
            radiusPassword: row.RadiusPassword,
            locationId: row.LocationId,
            cpiId: row.CpiId,
            port: row.Port,
            ntdId: row.NtdId,
            cvcId: row.CvcId,
            nbnOrderId: row.NbnOrderId,
            avcId: row.AvcId,
            priId: row.PriId,
            fibreUpgrade: row.FibreUpgrade,
          }));

          const res = await vm.bulkImportExistingOrderAsync({ orders: orders });

          const result = res?.data?.result || res?.data?.data || res?.data;
          const message = res?.data?.message || "";

          const baseMessage = message.split(".")[0].trim();

          const failedDetails = (result?.failedImports || result?.FailedImports || [])
            .map(f => ({
              orderId: f.nbnOrderId ?? f.NbnOrderId,
              reason: f.reason ?? f.Reason ?? ""
            }));

          vm.importResult = {
            totalProcessed: result?.totalProcessed ?? result?.TotalProcessed ?? 0,
            successCount: result?.successCount ?? result?.SuccessCount ?? 0,
            failureCount: result?.failureCount ?? result?.FailureCount ?? 0,
            failedDetails: failedDetails
          };
          vm.importMessage = baseMessage || `Processed ${vm.importResult.totalProcessed} orders: ${vm.importResult.successCount} succeeded, ${vm.importResult.failureCount} failed.`;
        } catch (error) {
          vm.$emit("bulkUploadError", error);
        } finally {
          vm.isSubmitting = false;
        }
      },
      downloadTemplate() {
        const header = MANDATORY_COLUMNS.join(",");
        const sampleValues = {
          WholesalerAccountNumber: "61596",
          CustomerFirstname: "John",
          CustomerSurname: "Doe",
          CustomerContactEmail: "john@example.com",
          CustomerContactNumber: "0412345678",
          CustomerReference: "REF001",
          CustomerAuthReference: "AUTH001",
          ProductType: "Fibre",
          OrderType: "Connect",
          CustomerAuthDate: "2026-02-14",
          BandwidthProfile: "D50_U20_Mbps_TC4_P",
          ContractTerm: "12",
          HasStaticIp: "No",
          RadiusUsername: "user@example.com",
          RadiusPassword: "password",
          LocationId: "LOC000000000001",
          CpiId: "N/A",
          Port: "N/A",
          NtdId: "N/A",
          CvcId: "N/A",
          NbnOrderId: "N/A",
          AvcId: "N/A",
          PriId: "N/A",
          FibreUpgrade: "No"
        };
        const sampleRow = MANDATORY_COLUMNS.map(col => sampleValues[col] ?? "").join(",");

        const csv = header + "\n" + sampleRow;
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "nbn_bulk_upload_template.csv";
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      close() {
        let vm = this;
        vm.csvFile = null;
        vm.fileError = "";
        vm.parsedRows = [];
        vm.importResult = null;
        vm.importMessage = "";
        vm.internalDialog = false;
      }
    }
  };
</script>

<style scoped>
  /* Remove the black "card" look */
  .failed-panels,
  .failed-panel,
  .failed-panel .v-expansion-panel-header,
  .failed-panel .v-expansion-panel-content,
  .failed-panel .v-expansion-panel-content__wrap {
    background: transparent !important;
    box-shadow: none !important;
  }

  /* Optional: add a subtle border so it still feels separated */
  .failed-panel {
    border: 1px solid rgba(255, 255, 255, 0.25) !important;
    border-radius: 6px;
    padding: 8px 12px;
  }

  /* Make the header/icon a red shade instead of black */
  .failed-header {
    color: #FFCDD2 !important; /* light red */
  }

    .failed-header .v-icon {
      color: #FFCDD2 !important;
    }

  /* Keep body text readable on red background */
  .failed-content {
    color: rgba(255, 255, 255, 0.95) !important;
  }
  </style>
